import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, type LucideIcon } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { cn } from './lib/cn';

export interface DropdownItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  /**
   * Renders in the icon slot instead of `icon`, for menus whose leading mark is
   * not a lucide glyph — the presence picker draws its real `<PresenceDot>`
   * there, because Busy and Do not disturb share one lucide icon and would
   * otherwise read as the same option twice.
   */
  iconNode?: ReactNode;
  /** Right-aligned hint, e.g. "Ctrl+N". */
  shortcut?: string;
  onSelect?: () => void;
  disabled?: boolean;
  /** Red text — Delete, Leave team, Remove. */
  destructive?: boolean;
  /** Shows a check when true (single/multi select menus). */
  checked?: boolean;
  /** Draws a hairline above this item. */
  separatorBefore?: boolean;
  /**
   * Nested items. The row becomes a submenu trigger (chevron, opens on hover or
   * ArrowRight) and its own `onSelect` is ignored — a row cannot both choose a
   * value and open a drawer.
   *
   * This is what keeps a long menu short: six presence states, a duration
   * picker and a reset belong BEHIND one "Available" row, not stacked in the
   * card until it runs off the bottom of the screen.
   */
  submenu?: readonly DropdownItem[];
}

export interface DropdownProps {
  /** Trigger element — must accept a ref. */
  trigger: ReactNode;
  items: readonly DropdownItem[];
  /** Optional heading at the top of the menu. */
  label?: string;
  /**
   * Arbitrary content pinned above the items — an identity block, a workspace
   * header, anything a flat `DropdownItem` cannot express.
   *
   * It is NOT a menu item: it sits outside the roving-focus ring, so the arrow
   * keys still move between real items only. Keep interactive controls in here
   * sparingly, and drive the menu's `open` state yourself if one needs to close it.
   */
  header?: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Menu width. Defaults to a comfortable 224px. */
  className?: string;
}

/** The leading mark. A fixed 16px slot so every label starts on one line. */
function ItemGlyph({ item }: { item: DropdownItem }) {
  if (item.iconNode !== undefined) {
    return <span className="flex h-4 w-4 shrink-0 items-center justify-center">{item.iconNode}</span>;
  }
  const Icon = item.icon;
  if (Icon === undefined) return null;
  return <Icon size={16} strokeWidth={2} className="shrink-0" aria-hidden />;
}

/** One row's shared skin, so an item and a submenu trigger cannot drift apart. */
const ROW = [
  'flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2 py-2 text-md font-medium outline-none transition-colors duration-fast',
  'data-[highlighted]:bg-subtle data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
].join(' ');

const tone = (item: DropdownItem): string =>
  item.destructive
    ? 'text-destructive data-[highlighted]:bg-destructive-subtle'
    : 'text-secondary-foreground data-[highlighted]:text-foreground';

/**
 * A surface that can be scrolled rather than clipped.
 *
 * Radix exposes the space it actually has as
 * `--radix-dropdown-menu-content-available-height`; without capping to it, a
 * menu taller than the viewport simply runs off the bottom of the screen and
 * its last items become unreachable.
 */
const SURFACE = [
  'z-dropdown min-w-56 overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-dropdown',
  'max-h-[var(--radix-dropdown-menu-content-available-height)]',
  'animate-scale-in',
  /* The menu manages its own roving focus and shows it by highlighting rows, so
     the global :focus-visible ring must not also draw a box round the card. */
  'focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
].join(' ');

function Rows({ items }: { items: readonly DropdownItem[] }) {
  return (
    <>
      {items.map((item) => (
        <div key={item.id}>
          {item.separatorBefore ? <Menu.Separator className="my-1 h-px bg-border" /> : null}

          {item.submenu !== undefined && item.submenu.length > 0 ? (
            <Menu.Sub>
              <Menu.SubTrigger
                disabled={item.disabled}
                className={cn(ROW, tone(item), 'data-[state=open]:bg-subtle')}
              >
                <ItemGlyph item={item} />
                <span className="flex-1 truncate">{item.label}</span>
                <ChevronRight size={14} strokeWidth={2.5} className="text-muted-foreground" aria-hidden />
              </Menu.SubTrigger>
              <Menu.Portal>
                <Menu.SubContent sideOffset={2} alignOffset={-4} className={SURFACE}>
                  <Rows items={item.submenu} />
                </Menu.SubContent>
              </Menu.Portal>
            </Menu.Sub>
          ) : (
            <Menu.Item disabled={item.disabled} onSelect={item.onSelect} className={cn(ROW, tone(item))}>
              <ItemGlyph item={item} />
              <span className="flex-1 truncate">{item.label}</span>
              {item.checked ? <Check size={14} strokeWidth={3} className="text-primary" aria-hidden /> : null}
              {item.shortcut ? (
                <span className="text-sm text-muted-foreground tabular">{item.shortcut}</span>
              ) : null}
            </Menu.Item>
          )}
        </div>
      ))}
    </>
  );
}

export function Dropdown({
  trigger, items, label, header, align = 'end', side = 'bottom', open, onOpenChange, className,
}: DropdownProps) {
  /**
   * WAS THIS MENU DRIVEN BY A POINTER?
   *
   * On close, Radix returns focus to the trigger. The browser then treats that
   * programmatic focus as keyboard-ish and matches `:focus-visible`, so a button
   * clicked with a mouse was left wearing a focus ring long after its menu had
   * gone — the purple box round the workspace chip.
   *
   * Suppressing the ring in CSS cannot fix this: the styling is right, the FOCUS
   * is wrong. A mouse user never asked for the caret back, so for a pointer-driven
   * close we decline the restore. Keyboard closes (Escape, Tab, arrows) still get
   * focus returned, because there the caret is the only way back.
   */
  const viaPointer = useRef(false);

  return (
    <Menu.Root open={open} onOpenChange={onOpenChange}>
      <Menu.Trigger asChild onPointerDown={() => { viaPointer.current = true; }}
        onKeyDown={() => { viaPointer.current = false; }}>
        {trigger}
      </Menu.Trigger>

      <Menu.Portal>
        <Menu.Content
          align={align}
          side={side}
          sideOffset={6}
          onKeyDown={() => {
            /* Escape or an arrow AFTER a mouse-open is still a keyboard exit,
               and a keyboard user needs the caret back where they left it. */
            viaPointer.current = false;
          }}
          onCloseAutoFocus={(event) => {
            if (viaPointer.current) event.preventDefault();
            viaPointer.current = false;
          }}
          className={cn(SURFACE, className)}
        >
          {header ?? null}

          {label ? (
            <Menu.Label className="px-2 py-1.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
              {label}
            </Menu.Label>
          ) : null}

          <Rows items={items} />
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
